// Downscale/recompress an image in the browser before uploading. Phone cameras
// produce 3–8 MB JPEGs; shipping those raw is slow on mobile data and wasteful
// in Storage. We cap the long edge and re-encode as JPEG, returning the
// original File if compression wouldn't help.
export async function compressImage(
  file: File,
  { maxDim = 1600, quality = 0.82 }: { maxDim?: number; quality?: number } = {},
): Promise<Blob> {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file; // decoding failed — upload the original
  }

  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  // Already small in both dimensions and bytes — not worth re-encoding.
  if (scale === 1 && file.size < 600 * 1024) {
    bitmap.close();
    return file;
  }

  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', quality),
  );
  // Keep whichever is smaller.
  return blob && blob.size < file.size ? blob : file;
}
